import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Button, TextButton } from '@safetyhub/ui';
import { ActionItem, useActionItemAcknowledge, useActionItems } from '../stores/actionItems';
import { BANNER_LAYER } from './modalLayers';

interface ActionItemsBarProps {
  /** 위쪽 배너들의 높이 합 — 겹치지 않게 그만큼 내려서 붙는다. */
  topOffset?: number;
  /** 실제 렌더 높이 보고 — Layout이 본문 오프셋을 이 값으로 쌓는다. */
  onHeightChange?: (height: number) => void;
}

/**
 * "확인이 필요한 항목" 상단 바.
 *
 * 결정 기록 `2026-08-19-banner-slot-policy-and-actionable-items.md` 3항 — 바는 **항상 자기
 * 내용을 보여준다**. 개수에 따라 달라지는 것은 **접힘 여부뿐**이다.
 *
 *  - 0건: 아무것도 렌더하지 않는다(높이 0 보고).
 *  - 1건: 접지 않고 **그대로 배너처럼** — 문구 + CTA가 직접 보인다(1건짜리에 카운트 + 펼치기는
 *    불필요한 한 단계다).
 *  - 2건+: 접힌 한 줄(`확인이 필요한 항목 N건`) + **그 자리에서** 펼친다. 벨로 넘기지 않는다 —
 *    누른 곳과 열리는 곳이 달라지기 때문이다(결정 기록 2항 「원안 정정」).
 *
 * `topOffset`/`onHeightChange` 관례는 `VerificationPendingBanner`와 같다(새 관례를 만들지 않는다).
 *
 * ⚠️ 항목 목록은 이 컴포넌트가 갖지 않는다 — 벨 상단 섹션과 **같은 `useActionItems()`**를 읽는다.
 */
export default function ActionItemsBar({ topOffset = 0, onHeightChange }: ActionItemsBarProps) {
  const items = useActionItems();
  const ref = useRef<HTMLDivElement>(null);
  /**
   * 기본값은 **접힘**이다(결정 기록 3항).
   *
   * 펼친 상태의 **세션 기억은 두지 않는다**(결정 기록이 「구현 시 판단」으로 위임한 축 — 팀장
   * 판단 2026-09-29). 바는 항목이 있는 동안 항상 뜨므로 접혀도 정보가 유실되지 않고, 상태를
   * 하나 줄이는 편이 낫다.
   */
  const [expanded, setExpanded] = useState(false);
  /**
   * 「확인했음」 실행·busy·실패 토스트는 **벨 섹션과 같은 한 벌**이다
   * (`useActionItemAcknowledge`). 여기서 따로 `try/catch`를 들고 있었더니 실패가 **완전히
   * 조용해서**(벨만 토스트) 「버튼이 고장났다」로 읽히고 반복 클릭이 요청을 매번 냈다
   * (2026-10-05 리뷰).
   */
  const { busyItemId, run: runAcknowledge } = useActionItemAcknowledge();

  const shouldRender = items.length > 0;
  const collapsible = items.length > 1;

  useEffect(() => {
    if (!shouldRender) onHeightChange?.(0);
  }, [shouldRender, onHeightChange]);

  // 항목이 1건으로 줄면 접힘 개념 자체가 없어지므로 펼침 상태를 되돌린다(다시 2건이 될 때
  // 지난 펼침이 되살아나지 않게).
  useEffect(() => {
    if (!collapsible) setExpanded(false);
  }, [collapsible]);

  useLayoutEffect(() => {
    if (!shouldRender || !ref.current) return;
    const element = ref.current;
    const update = () => onHeightChange?.(element.offsetHeight);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => observer.disconnect();
  }, [shouldRender, onHeightChange]);

  if (!shouldRender) return null;

  const renderItemActions = (item: ActionItem) => (
    <div className="flex items-center gap-2 flex-shrink-0">
      <Button variant="secondary" size="sm" onClick={item.cta.run}>
        {item.cta.label}
      </Button>
      {item.acknowledge && (
        <TextButton
          tone="neutral"
          className="text-xs"
          disabled={busyItemId === item.id}
          onClick={() => runAcknowledge(item)}
        >
          {item.acknowledge.label}
        </TextButton>
      )}
    </div>
  );

  /**
   * z는 **차단성 배너 아래**다 — 헤더 `z-[60]`, 빙의 `z-[100]`, 기관 인증보다 낮아야 한다(차단성이 위).
   * 값과 순서의 정본은 `modalLayers`의 `BANNER_LAYER`다.
   */
  return (
    <div
      ref={ref}
      className={`fixed left-0 right-0 ${BANNER_LAYER.actionItems} bg-amber-50 border-b border-amber-200`}
      data-testid="action-items-bar"
      style={{ top: topOffset }}
    >
      <div className="max-w-7xl mx-auto px-4 py-2.5 sm:px-6 lg:px-8">
        {collapsible ? (
          <>
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm font-medium text-amber-800 flex-1 min-w-0 truncate">
                확인이 필요한 항목 {items.length}건
              </p>
              <TextButton
                className="text-xs flex-shrink-0"
                aria-expanded={expanded}
                onClick={() => setExpanded((v) => !v)}
              >
                {expanded ? '접기' : '펼치기'}
              </TextButton>
            </div>
            {expanded && (
              <ul className="mt-2 space-y-2 border-t border-amber-200 pt-2">
                {items.map((item) => (
                  <li key={item.id} className="flex items-center justify-between flex-wrap gap-2">
                    <span className="text-sm text-amber-800 flex-1 min-w-0 truncate">
                      {item.barLabel}
                    </span>
                    {renderItemActions(item)}
                  </li>
                ))}
              </ul>
            )}
          </>
        ) : (
          <div className="flex items-center justify-between flex-wrap gap-2">
            <p className="text-sm font-medium text-amber-800 flex-1 min-w-0 truncate">
              {items[0].barLabel}
            </p>
            {renderItemActions(items[0])}
          </div>
        )}
      </div>
    </div>
  );
}
